import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    ColorFinderState,
    createMockVideoColorFinderJob,
    deleteMockVideoColorFinderJob,
    selectUploadsForSet,
    selectUploadsForSetSummary,
} from "./ColorFinderSlice"
import { JobStatusType } from "@nova/constants"
import { get, noop } from "nodash"
import { parseTelemetryFile } from "plugins/Video/FMV/telemetry"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { compress } from "../../shared/compression"
import { VideoMetadata } from "@nova/slice-video"
import { useMemo } from "react"
import { useUniversalControl } from "plugins/shared/useUniversalControl"
import { createVideoColorFinderJob } from "features/pubsub/entityActions/jobs/videoColorFinder"
import { stagedFiles } from "@nova/slice-multifile-upload"
import { selectActiveLayerId } from "panels/content/side-panel/slice.new"
import { createCumulativeRgb } from "@nova/component-color-finder-legend/src/web-workers/createCumulativeRgb/createCumulativeRgb"
import { buildColorIndex } from "@nova/component-color-finder-legend/src/colorspace"
import { TILE_SIZE_U32 } from "../processing/SuperIndexController.class"
import { uploadAnomalyData } from "../ImageColorFinder/ImageColorFinderUploads"
import { emitLayerProcessed } from "../emitLayerProcessed"
import { updateVideoColorFinderFlight } from "features/pubsub/entityActions/flights/videoColorFinder"
import { generateColorIndex } from "../V2/generation/generateColorIndex"
import { generateColorIndexFromLayer } from "../V2/generation/generateColorIndexFromLayer"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"

export const useColorFinderVideoProcessing = (setProgress?: (progresses: number[]) => void) => {
    const progresses = useMemo(() => [0, 0], [])
    const dispatch = useAppDispatch()
    const fireId = useAppSelector(selectActivatedFireId)!
    const workspaceId = useAppSelector(selectActiveWorkspaceId())!
    const activeLayerId = useAppSelector(selectActiveLayerId) || ""
    const summary = useAppSelector(selectUploadsForSetSummary(activeLayerId))
    const uploads = useAppSelector(selectUploadsForSet(activeLayerId))
    const { addLocalVideo, getLocalVideo } = useUniversalControl()

    const currentProjectContext = { activeLayerId, fireId, workspaceId }

    const videoFile = uploads?.find((upload) =>
        ((get(upload, "s3Key") as string | undefined) || "").toLowerCase().includes(".mp4")
    ) as ColorFinderState
    const srtFile = uploads?.find(
        (upload) =>
            !((get(upload, "s3Key") as string | undefined) || "").toLowerCase().includes(".mp4")
    )

    const file = stagedFiles[videoFile?.id]

    const updateProgress = (key: number) => (progress: number) => {
        progresses[key] = progress

        setProgress?.(progresses)
    }

    const createProgressTracker = () => {
        let videoLoaded = 0
        let videoTotal = 0
        let colorLoaded = 0
        let colorTotal = 0

        const update = () => {
            const total = videoTotal + colorTotal
            const loaded = videoLoaded + colorLoaded
            const progress = total > 0 ? loaded / total : 0

            progresses[ProgressType.Uploading] = progress
            setProgress?.(progresses)
        }

        return {
            onVideoProgress: (loaded: number, total: number) => {
                videoLoaded = loaded
                videoTotal = total
                update()
            },
            onColorProgress: (loaded: number, total: number) => {
                colorLoaded = loaded
                colorTotal = total
                update()
            },
            // Call this when we know the color data size but haven't started uploading yet
            // to ensure the total reflects the future work
            registerColorTotal: (total: number) => {
                colorTotal = total
                update()
            },
        }
    }

    const updateProcessingProgress = (progress: number) => {
        progresses[ProgressType.Processing] = progress
        setProgress?.(progresses)
    }

    const uploadCallback = async (flightId: string) => {
        const { colorData, metadata, anomalyIndex } = getLocalVideo(flightId)
        const superIndex = colorData?.at(-1)

        if (!colorData || !superIndex) {
            console.error("No local colorData for flight", { colorData })
            return
        }

        const tracker = createProgressTracker()

        if (colorData) {
            tracker.registerColorTotal(getColorDataSize(colorData))
        }

        const colorIndex = superIndex && buildColorIndex(superIndex)
        const cumulativeIndex = await createCumulativeRgb({ colorIndex })
        const numFrames = colorIndex.length

        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = true
            return true
        }
        window.addEventListener("beforeunload", onBeforeUnload)
        const start = performance.now()

        try {
            addLocalVideo(activeLayerId, {
                videoFile: file,
                colorData,
                metadata,
                cumulativeRgb: cumulativeIndex,
                anomalyIndex,
            })
            dispatch(
                createMockVideoColorFinderJob({
                    mock: true,
                    id: activeLayerId,
                    status: JobStatusType.STARTED,
                    upload: true,
                    name: file.name,
                })
            )

            const { videoPath, videoUploadPromise } = await uploadVideo(
                file,
                currentProjectContext,
                tracker.onVideoProgress
            )
            dispatch(
                createMockVideoColorFinderJob({
                    mock: true,
                    id: activeLayerId,
                    status: JobStatusType.MOCKCOMPLETED,
                    upload: true,
                    name: file.name,
                })
            )

            await videoUploadPromise

            const [srtPath, metadataPath, colordataPath, anomalyPath] = await Promise.all([
                uploadVideoData(stagedFiles[srtFile?.id || ""], currentProjectContext),
                metadata && uploadCompressedVideoData(metadata, currentProjectContext),
                uploadColorData(colorData, currentProjectContext, tracker.onColorProgress),
                uploadAnomalyData(anomalyIndex, { flightId, projectId: fireId, workspaceId }),
            ])

            progresses[ProgressType.Uploading] = 1
            setProgress?.(progresses)

            dispatch(
                createVideoColorFinderJob({
                    data: {
                        srtPath,
                        metadataPath,
                        colordataPath,
                        anomalyPath,
                        videoPath,
                        numFrames,
                        cumulativeColorIndex: new Uint8Array(
                            cumulativeIndex?.cumulativeIndex.buffer,
                            cumulativeIndex?.cumulativeIndex.byteOffset,
                            cumulativeIndex?.cumulativeIndex.byteLength
                        ),
                    },
                    context: {
                        flightId: activeLayerId,
                        file: "video.mp4",
                    },
                })
            )

            const lng = metadata?.[0][0]
            const lat = metadata?.[0][1]
            if (lng !== undefined && lat !== undefined) {
                dispatch(
                    updateVideoColorFinderFlight({
                        id: activeLayerId,
                        updates: { startingPosition: { latitude: lat, longitude: lng } },
                    })
                )
            }

            emitLayerProcessed(workspaceId, fireId, activeLayerId, "videoColorFinderFlights", true)
        } catch (err) {
            console.error("@color-finder", err)
            dispatch(
                createMockVideoColorFinderJob({
                    mock: true,
                    id: activeLayerId,
                    name: "mock",
                    status: JobStatusType.FAILED,
                    upload: true,
                })
            )
            emitLayerProcessed(workspaceId, fireId, activeLayerId, "videoColorFinderFlights", false)
            throw "Color finder failed to upload"
        } finally {
            window.removeEventListener("beforeunload", onBeforeUnload)
            console.info("color finder exited at", performance.now() - start)
        }
    }

    const processCallback = async (context: unknown) => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = true
            return true
        }
        window.addEventListener("beforeunload", onBeforeUnload)

        const tracker = createProgressTracker()
        const start = performance.now()
        const shouldUpload = !!get(context, "upload")

        try {
            dispatch(
                createMockVideoColorFinderJob({
                    mock: true,
                    id: activeLayerId,
                    status: JobStatusType.STARTED,
                    upload: shouldUpload,
                    name: file.name,
                })
            )

            const colorDataPromise = generateColorIndex(file, { updateProcessingProgress })
            const metadataPromise = parseTelemetryFile(stagedFiles[srtFile?.id || ""])

            if (!shouldUpload) {
                const { superIndex: colorData, anomalyScores } = await colorDataPromise
                const metadata = await metadataPromise
                const colorIndex = buildColorIndex(colorData.at(-1)!)
                const cumulativeIndex = await createCumulativeRgb({ colorIndex })

                updateProcessingProgress(1)

                addLocalVideo(activeLayerId, {
                    videoFile: file,
                    colorData,
                    metadata,
                    cumulativeRgb: cumulativeIndex,
                    anomalyIndex: anomalyScores,
                })
                dispatch(
                    createMockVideoColorFinderJob({
                        mock: true,
                        id: activeLayerId,
                        status: JobStatusType.QUEUED,
                        upload: shouldUpload,
                        name: file.name,
                    })
                )
                return
            }

            const uploadVideoPromise = uploadVideo(
                file,
                currentProjectContext,
                tracker.onVideoProgress
            )
            const { videoPath, videoUploadPromise } = await uploadVideoPromise
            const metadata = await metadataPromise
            const { superIndex: colorData, anomalyScores } = await colorDataPromise

            const superIndex = colorData.at(-1)
            if (!superIndex) {
                console.error("No colorData returned from colorDataPromise", { colorData })
                return
            }

            const colorIndex = buildColorIndex(superIndex)
            const numFrames = colorIndex.length

            tracker.registerColorTotal(getColorDataSize(colorData))

            const cumulativeIndex = await createCumulativeRgb({ colorIndex })

            addLocalVideo(activeLayerId, {
                videoFile: file,
                colorData,
                metadata,
                cumulativeRgb: cumulativeIndex,
                anomalyIndex: anomalyScores,
            })

            dispatch(
                createMockVideoColorFinderJob({
                    mock: true,
                    id: activeLayerId,
                    status: JobStatusType.MOCKCOMPLETED,
                    upload: shouldUpload,
                    name: file.name,
                })
            )
            await videoUploadPromise

            const [srtPath, metadataPath, colordataPath, anomalyPath] = await Promise.all([
                uploadVideoData(stagedFiles[srtFile?.id || ""], currentProjectContext),
                metadata && uploadCompressedVideoData(metadata, currentProjectContext),
                uploadColorData(colorData, currentProjectContext, tracker.onColorProgress),
                uploadAnomalyData(anomalyScores, {
                    flightId: activeLayerId,
                    projectId: fireId,
                    workspaceId,
                }),
            ])

            progresses[ProgressType.Uploading] = 1
            setProgress?.(progresses)

            dispatch(
                createVideoColorFinderJob({
                    data: {
                        srtPath,
                        metadataPath,
                        colordataPath,
                        anomalyPath,
                        videoPath,
                        numFrames,
                        cumulativeColorIndex: new Uint8Array(
                            cumulativeIndex?.cumulativeIndex.buffer,
                            cumulativeIndex?.cumulativeIndex.byteOffset,
                            cumulativeIndex?.cumulativeIndex.byteLength
                        ),
                    },
                    context: {
                        flightId: activeLayerId,
                        file: "video.mp4",
                    },
                })
            )

            const lng = metadata?.[0][0]
            const lat = metadata?.[0][1]
            if (lng !== undefined && lat !== undefined) {
                dispatch(
                    updateVideoColorFinderFlight({
                        id: activeLayerId,
                        updates: { startingPosition: { latitude: lat, longitude: lng } },
                    })
                )
            }

            emitLayerProcessed(workspaceId, fireId, activeLayerId, "videoColorFinderFlights", true)
        } catch (err) {
            console.error("@color-finder", err)
            dispatch(
                createMockVideoColorFinderJob({
                    mock: true,
                    id: activeLayerId,
                    name: "mock",
                    status: JobStatusType.FAILED,
                    upload: shouldUpload,
                })
            )
            emitLayerProcessed(workspaceId, fireId, activeLayerId, "videoColorFinderFlights", false)
            throw "Color finder failed to process"
        } finally {
            window.removeEventListener("beforeunload", onBeforeUnload)
            console.info("color finder exited at", performance.now() - start)
        }
    }

    const processFromLayerCallback = async (
        apiRoute: string,
        flightId: string,
        paths: { videoPath: string; metadataPath?: string; srtPath?: string },
        abortController: AbortController
    ) => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = true
            return true
        }
        window.addEventListener("beforeunload", onBeforeUnload)

        // Define the split: 90% for generation, 10% for upload
        const GENERATION_PHASE_WEIGHT = 0.9
        const UPLOAD_PHASE_WEIGHT = 1 - GENERATION_PHASE_WEIGHT

        try {
            // Dispatch STARTED
            dispatch(
                createMockVideoColorFinderJob({
                    mock: true,
                    id: activeLayerId,
                    status: JobStatusType.STARTED,
                    upload: true,
                    name: paths.videoPath.split("/").pop() || "Layer Video",
                })
            )

            const colorDataPromise = generateColorIndexFromLayer(apiRoute, {
                updateProcessingProgress: (v) =>
                    updateProgress(ProgressType.Processing)(v * GENERATION_PHASE_WEIGHT),
                abortController,
            })

            const { anomalyScores, superIndex: colorData } = await colorDataPromise

            const superIndex = colorData.at(-1)!
            const colorIndex = buildColorIndex(superIndex)
            if (!colorIndex) {
                console.error("No colorData returned from colorDataPromise", { colorData })
                return
            }

            const onUploadProgress = (uploaded: number, total: number) => {
                const uploadPercentage = total > 0 ? uploaded / total : 0
                const combinedProgress =
                    GENERATION_PHASE_WEIGHT + uploadPercentage * UPLOAD_PHASE_WEIGHT
                updateProgress(ProgressType.Processing)(combinedProgress)
            }

            const [colordataPath, cumulativeIndex, anomalyPath] = await Promise.all([
                // Pass our scaled callback to the uploader
                uploadColorData(colorData, currentProjectContext, onUploadProgress),
                createCumulativeRgb({ colorIndex }),
                uploadAnomalyData(anomalyScores, { flightId, projectId: fireId, workspaceId }),
            ])

            dispatch(
                createVideoColorFinderJob({
                    data: {
                        srtPath: paths.srtPath,
                        metadataPath: paths.metadataPath,
                        colordataPath,
                        anomalyPath: anomalyPath,
                        videoPath: paths.videoPath,
                        numFrames: colorIndex.length,
                        cumulativeColorIndex: new Uint8Array(
                            cumulativeIndex?.cumulativeIndex.buffer,
                            cumulativeIndex?.cumulativeIndex.byteOffset,
                            cumulativeIndex?.cumulativeIndex.byteLength
                        ),
                    },
                    context: {
                        flightId: activeLayerId,
                        file: paths.videoPath,
                    },
                })
            )

            dispatch(deleteMockVideoColorFinderJob(activeLayerId))

            updateProgress(ProgressType.Processing)(1)

            emitLayerProcessed(workspaceId, fireId, activeLayerId, "videoColorFinderFlights", true)
        } catch (err) {
            console.error("@color-finder-layer", err)
            dispatch(
                createMockVideoColorFinderJob({
                    mock: true,
                    id: activeLayerId,
                    name: paths.videoPath,
                    status: JobStatusType.FAILED,
                    upload: true,
                })
            )
            emitLayerProcessed(workspaceId, fireId, activeLayerId, "videoColorFinderFlights", false)
        } finally {
            window.removeEventListener("beforeunload", onBeforeUnload)
        }
    }

    const canProcessPlugin = (baseRequirements: boolean) =>
        baseRequirements && !!summary?.uploadCount && summary.uploadCount >= 1

    return {
        processFromLayerCallback,
        canProcessPlugin,
        processCallback,
        uploadCallback,
        dependencies: [dispatch, uploads, fireId],
    }
}

const uploadVideoData = async (
    srtFile: File | undefined,
    {
        workspaceId,
        fireId,
        activeLayerId,
    }: { workspaceId: string; fireId: string; activeLayerId: string }
) => {
    if (!srtFile) return undefined
    const metadataPath = key(workspaceId, fireId, activeLayerId, "metadata.srt")
    await fileManagementApi.putFile(srtFile, { target: metadataPath })
    return metadataPath
}

const uploadCompressedVideoData = async (
    metadata: VideoMetadata,
    {
        workspaceId,
        fireId,
        activeLayerId,
    }: { workspaceId: string; fireId: string; activeLayerId: string }
) => {
    const data = await compress(metadata, "msg")
    const metadataPath = key(workspaceId, fireId, activeLayerId, "metadata.msg")
    await fileManagementApi.putFile(data, { target: metadataPath })
    return metadataPath
}

// should go into its own file in the image color finder plugin
export const useColorFinderImageProcessing = () => {
    const dispatch = useAppDispatch()
    const fireId = useAppSelector(selectActivatedFireId) || ""
    const activeLayerId = useAppSelector(selectActiveLayerId) || ""
    const uploads = useAppSelector(selectUploadsForSet(activeLayerId))

    const canProcessPlugin = () => {
        return false
    }

    return {
        canProcessPlugin,
        processCallback: noop,
        dependencies: [dispatch, uploads, fireId, activeLayerId],
    }
}

export const buildTilePath = (base: string, zoom: number, tileNum: number) =>
    `${base}/${zoom}/${tileNum}.bin`

export const key = (
    organizationID: string,
    fireID: string,
    flightID: string,
    filename?: string
) => {
    return `public/org/${organizationID}/fire/${fireID}/flight/${flightID}/video${
        filename ? `/${filename}` : ""
    }`
}

enum ProgressType {
    Processing = 0,
    Uploading = 1,
}

const uploadVideo = async (
    video: File,
    {
        workspaceId,
        fireId,
        activeLayerId,
    }: { workspaceId: string; fireId: string; activeLayerId: string },
    onProgress?: (loaded: number, total: number) => void
) => {
    const videoPath = key(workspaceId, fireId, activeLayerId, "video.mp4")
    const promise = fileManagementApi.putFile(video, { target: videoPath, onProgress })

    return { videoPath, videoUploadPromise: promise }
}

const getColorDataSize = (colorIndex: Uint32Array[]) => {
    return colorIndex.reduce((acc, container) => acc + container.byteLength, 0)
}

const uploadColorData = async (
    colorIndex: Uint32Array[],
    {
        workspaceId,
        fireId,
        activeLayerId,
    }: { workspaceId: string; fireId: string; activeLayerId: string },
    onProgress?: (loaded: number, total: number) => void
) => {
    // We track progress based on uncompressed bytes processed, as compression size varies
    const totalBytes = getColorDataSize(colorIndex)
    let uploadedBytes = 0

    // Report initial state (0 loaded, known total)
    onProgress?.(0, totalBytes)

    for (let i = 0; i < colorIndex.length; i++) {
        const container = colorIndex[i]
        const tilesInContainer = container.length / TILE_SIZE_U32

        for (let tileNum = 0; tileNum < tilesInContainer; tileNum++) {
            const tilePath = key(workspaceId, fireId, activeLayerId, `${i + 1}/${tileNum}.bin`)
            const startIndex = tileNum * TILE_SIZE_U32
            const endIndex = startIndex + TILE_SIZE_U32

            const fixedSizeTile = new Uint32Array(TILE_SIZE_U32)
            const partialTileData = container.subarray(startIndex, endIndex)
            fixedSizeTile.set(partialTileData)

            const tileDataBytes = new Uint8Array(
                fixedSizeTile.buffer,
                fixedSizeTile.byteOffset,
                fixedSizeTile.byteLength
            )

            const tile = await compress(tileDataBytes, "gzip")
            try {
                await fileManagementApi.putFile(new Uint8Array(tile), { target: tilePath })

                uploadedBytes += fixedSizeTile.byteLength
                onProgress?.(uploadedBytes, totalBytes)
            } catch (err) {
                console.error(`Failed to upload tile ${tilePath}:`, err)
            }
        }
    }

    // Ensure we hit 100% (loaded == total) at the end
    onProgress?.(totalBytes, totalBytes)

    const colorDataPath = key(workspaceId, fireId, activeLayerId)
    return colorDataPath
}
